import { i18n } from "@lingui/core";
import dayjs from "dayjs";
import { MRT_Row } from "mantine-react-table";
import * as XLSX from "xlsx";

import { GetBookingsItem } from "@/shared/api/generated/schemas";
import { countryListnbNO } from "@/shared/utils/countryList";
import { langugeListnbNO } from "@/shared/utils/languageList";

import { bookingPaymentFormLabels } from "../bookings/utils/payment-form";
import { bookingPaymentFormDrinksLabels } from "../bookings/utils/payment-form-drinks";
import { bookingPaymentStatusLabels } from "../bookings/utils/payment-status";
import { bookingStatusLabels } from "../bookings/utils/status";
import { bookingVisitCategoryLabels } from "../bookings/utils/visit-category";

// Lookup maps for country and language names
const countryMap = new Map(countryListnbNO.map((c) => [c.value, c.label]));
const languageMap = new Map(langugeListnbNO.map((l) => [l.value, l.label]));

// Map table column IDs to export column keys
type ExportColumnKey =
  | "id"
  | "createdAt"
  | "createdBy"
  | "updatedAt"
  | "updatedBy"
  | "status"
  | "location"
  | "department"
  | "arrival"
  | "departure"
  | "visitCategory"
  | "customerId"
  | "customer"
  | "paymentCategory"
  | "paymentCategoryDrinks"
  | "paymentStatus"
  | "sentToPowerOfficeDate"
  | "group"
  | "contactPerson"
  | "contactPersonPhone"
  | "nationalities"
  | "languages"
  | "bookedTotal"
  | "bookedTravelLeader"
  | "arrivedTotal"
  | "arrivedAdults"
  | "arrivedStaff"
  | "arrivedChildrenFree"
  | "arrivedChildrenPaid"
  | "guide"
  | "externalInformation"
  | "internalInformation"
  | "program"
  | "productCategories"
  | "products";

const COLUMN_KEY_TO_TABLE_ID: Record<ExportColumnKey, string> = {
  id: "id",
  status: "status",
  location: "location.name",
  department: "department.name",
  arrival: "arrival",
  departure: "departure",
  visitCategory: "visitCategory",
  customerId: "customer.id",
  customer: "customer.name",
  paymentCategory: "paymentCategory",
  paymentCategoryDrinks: "paymentCategoryDrinks",
  paymentStatus: "paymentStatus",
  sentToPowerOfficeDate: "sentToPowerOfficeDate",
  group: "group.name",
  contactPerson: "group.contactPerson",
  contactPersonPhone: "group.phone",
  nationalities: "nationalities",
  languages: "languages",
  bookedTotal: "group.guests.booked",
  bookedTravelLeader: "group.travelLeader.booked",
  arrivedTotal: "arrivedTotal",
  arrivedAdults: "group.arrivals.adults",
  arrivedStaff: "group.arrivals.staff",
  arrivedChildrenFree: "group.arrivals.childrenFree",
  arrivedChildrenPaid: "group.arrivals.childrenPaid",
  guide: "guide",
  externalInformation: "externalInformation",
  internalInformation: "internalInformation",
  program: "program",
  productCategories: "productCategories",
  products: "products",
  createdAt: "createdAt",
  createdBy: "createdBy",
  updatedAt: "updatedAt",
  updatedBy: "updatedBy",
};

// Helper function to create date cell objects
const createDateCell = (
  dateString: string | null | undefined,
): XLSX.CellObject | string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return {
    t: "d", // Cell type: date
    v: date, // Value: Date object
    z: "d.m.yyyy hh:mm", // Format: Norwegian date format with time (HH for 24-hour format)
  };
};

// Helper function to create number cell objects
const createNumberCell = (
  value: number | null | undefined,
  format?: string,
): XLSX.CellObject | string => {
  if (value === null || value === undefined) return "";
  return {
    t: "n", // Cell type: number
    v: value, // Value: number
    z: format, // Optional number format
  };
};

// Helper function to create currency cell objects (Norwegian Kroner)
const createCurrencyCell = (
  value: number | null | undefined,
): XLSX.CellObject | string => {
  if (value === null || value === undefined) return "";
  return {
    t: "n", // Cell type: number
    v: value, // Value: number
    z: '#,##0.00 "kr"', // Norwegian currency format
  };
};

export function downloadReportAsExcelFile(
  rows: MRT_Row<GetBookingsItem>[],
  visibleTableColumnIds: string[],
  categoryIdToName?: Record<string, string>,
) {
  const rowData = rows.map((row) => row.original);

  // Create reverse mapping from table ID to export column keys
  const tableIdToColumnKeys = new Map<string, ExportColumnKey[]>();
  Object.entries(COLUMN_KEY_TO_TABLE_ID).forEach(([key, tableId]) => {
    if (!tableIdToColumnKeys.has(tableId)) {
      tableIdToColumnKeys.set(tableId, []);
    }
    tableIdToColumnKeys.get(tableId)!.push(key as ExportColumnKey);
  });

  // Determine which export columns should be included based on visible table columns
  const visibleExportColumns = new Set<ExportColumnKey>();
  visibleTableColumnIds.forEach((tableId) => {
    const exportKeys = tableIdToColumnKeys.get(tableId);
    if (exportKeys) {
      exportKeys.forEach((key) => visibleExportColumns.add(key));
    }
  });

  // Define all possible columns with their configurations
  const allColumnsConfig: Record<
    ExportColumnKey,
    {
      header: string;
      width: number;
      getValue: (booking: GetBookingsItem) => XLSX.CellObject | string | number;
    }
  > = {
    id: {
      header: "Bookingnummer",
      width: 10,
      getValue: (booking) => createNumberCell(booking.id),
    },
    createdAt: {
      header: "Dato opprettet",
      width: 14,
      getValue: (booking) => createDateCell(booking.createdAt),
    },
    createdBy: {
      header: "Opprettet av",
      width: 10,
      getValue: (booking) =>
        booking.createdBy
          ? `${booking.createdBy.first_name ?? ""} ${booking.createdBy.last_name ?? ""}`.trim()
          : "",
    },
    updatedAt: {
      header: "Dato oppdatert",
      width: 14,
      getValue: (booking) => createDateCell(booking.updatedAt),
    },
    updatedBy: {
      header: "Oppdatert av",
      width: 10,
      getValue: (booking) =>
        booking.updatedBy
          ? `${booking.updatedBy.first_name ?? ""} ${booking.updatedBy.last_name ?? ""}`.trim()
          : "",
    },
    status: {
      header: "Bookingstatus",
      width: 10,
      getValue: (booking) =>
        booking.status ? i18n.t(bookingStatusLabels[booking.status]) : "",
    },
    location: {
      header: "Lokasjon",
      width: 10,
      getValue: (booking) => booking.location?.name ?? "",
    },
    department: {
      header: "Avdeling",
      width: 10,
      getValue: (booking) => booking.department?.name ?? "",
    },
    arrival: {
      header: "Ankomst",
      width: 14,
      getValue: (booking) => createDateCell(booking.arrival),
    },
    departure: {
      header: "Avreise",
      width: 14,
      getValue: (booking) => createDateCell(booking.departure),
    },
    visitCategory: {
      header: "Besøkskategori",
      width: 10,
      getValue: (booking) =>
        booking.visitCategory
          ? i18n.t(bookingVisitCategoryLabels[booking.visitCategory])
          : "",
    },
    customerId: {
      header: "Kunde ID",
      width: 10,
      getValue: (booking) => createNumberCell(booking.customer?.id),
    },
    customer: {
      header: "Kunde",
      width: 10,
      getValue: (booking) => booking.customer?.name ?? "",
    },
    paymentCategory: {
      header: "Betalingsform",
      width: 10,
      getValue: (booking) =>
        booking.paymentCategory
          ? i18n.t(bookingPaymentFormLabels[booking.paymentCategory])
          : "",
    },
    paymentCategoryDrinks: {
      header: "Betalingsform drikke",
      width: 10,
      getValue: (booking) =>
        booking.paymentCategoryDrinks
          ? i18n.t(
              bookingPaymentFormDrinksLabels[booking.paymentCategoryDrinks],
            )
          : "",
    },
    paymentStatus: {
      header: "Betalingsstatus",
      width: 10,
      getValue: (booking) =>
        booking.paymentStatus
          ? i18n.t(bookingPaymentStatusLabels[booking.paymentStatus])
          : "",
    },
    sentToPowerOfficeDate: {
      header: "Sent til PowerOffice dato",
      width: 14,
      getValue: (booking) => createDateCell(booking.sentToPowerOfficeDate),
    },
    group: {
      header: "Gruppe",
      width: 10,
      getValue: (booking) => booking.group?.name ?? "",
    },
    contactPerson: {
      header: "Kontaktperson",
      width: 10,
      getValue: (booking) => booking.group?.contactPerson ?? "",
    },
    contactPersonPhone: {
      header: "Kontaktperson mobil",
      width: 10,
      getValue: (booking) => booking.group?.phone ?? "",
    },
    nationalities: {
      header: "Nasjonalitet",
      width: 10,
      getValue: (booking) =>
        booking.group?.nationalities
          ?.map((code) => countryMap.get(code) ?? code)
          .join(", ") ?? "",
    },
    languages: {
      header: "Språk",
      width: 10,
      getValue: (booking) =>
        booking.group?.languages
          ?.map((code) => languageMap.get(code) ?? code)
          .join(", ") ?? "",
    },
    bookedTotal: {
      header: "Forventet antall totalt",
      width: 10,
      getValue: (booking) =>
        createNumberCell(
          (booking.group?.guests?.booked ?? 0) +
            (booking.group?.travelLeader?.booked ?? 0),
        ),
    },
    bookedTravelLeader: {
      header: "Forventet reiseleder/sjåfør",
      width: 10,
      getValue: (booking) =>
        createNumberCell(booking.group?.travelLeader?.booked),
    },
    arrivedTotal: {
      header: "Ankommet totalt",
      width: 10,
      getValue: (booking) => createNumberCell(booking.group?.arrivals?.sum),
    },
    arrivedAdults: {
      header: "Ankommet voksne",
      width: 10,
      getValue: (booking) => createNumberCell(booking.group?.arrivals?.adults),
    },
    arrivedStaff: {
      header: "Ankommet reiseleder/sjåfør",
      width: 10,
      getValue: (booking) => createNumberCell(booking.group?.arrivals?.staff),
    },
    arrivedChildrenFree: {
      header: "Ankommet barn (gratis)",
      width: 10,
      getValue: (booking) =>
        createNumberCell(booking.group?.arrivals?.childrenFree),
    },
    arrivedChildrenPaid: {
      header: "Ankommet barn (betalende)",
      width: 10,
      getValue: (booking) =>
        createNumberCell(booking.group?.arrivals?.childrenPaid),
    },
    guide: {
      header: "Guide",
      width: 10,
      getValue: (booking) =>
        booking.guide
          ? `${booking.guide.firstName} ${booking.guide.lastName ?? ""}`.trim()
          : "",
    },
    externalInformation: {
      header: "Ekstern informasjon",
      width: 10,
      getValue: (booking) => booking.externalInformation ?? "",
    },
    internalInformation: {
      header: "Intern informasjon",
      width: 10,
      getValue: (booking) => booking.internalInformation ?? "",
    },
    program: {
      header: "Program",
      width: 10,
      getValue: (booking) =>
        booking.program
          ?.filter((p) => p.productID === null)
          .map((p) => {
            const name = p.comment ?? "";
            if (p.start && p.end) {
              const startTime = dayjs(p.start).format("HH:mm");
              const endTime = dayjs(p.end).format("HH:mm");
              return `${startTime}-${endTime} ${name}`;
            }
            return name;
          })
          .join("\n") ?? "",
    },
    productCategories: {
      header: "Produktkategorier",
      width: 10,
      getValue: (booking) => {
        const categoryIds =
          booking.program?.map((p) => p.productCategoryID)?.filter(Boolean) ??
          [];

        const categoryNames = Array.from(
          new Set(
            categoryIds.map((id) => {
              const key = String(id);
              return categoryIdToName?.[key] ?? key;
            }),
          ),
        );

        return categoryNames.join(", ");
      },
    },
    products: {
      header: "Produkter",
      width: 10,
      getValue: () => "", // Will be handled separately with dynamic columns
    },
  };

  // Determine if products should be included
  const includeProducts = visibleExportColumns.has("products");

  // Filter columns to only visible ones
  const baseColumns: { header: string; width: number }[] = [];
  const columnKeys: ExportColumnKey[] = [];

  (Object.keys(allColumnsConfig) as ExportColumnKey[]).forEach((key) => {
    if (
      key !== "products" &&
      key !== "productCategories" &&
      visibleExportColumns.has(key)
    ) {
      baseColumns.push({
        header: allColumnsConfig[key].header,
        width: allColumnsConfig[key].width,
      });
      columnKeys.push(key);
    }
  });

  // Add productCategories if visible
  if (visibleExportColumns.has("productCategories")) {
    baseColumns.push({
      header: allColumnsConfig.productCategories.header,
      width: allColumnsConfig.productCategories.width,
    });
    columnKeys.push("productCategories");
  }

  // Handle products if needed
  let maxProducts = 0;
  if (includeProducts) {
    maxProducts = Math.max(
      ...rowData.map(
        (booking) =>
          booking.program?.filter((p) => p.productID !== null).length ?? 0,
      ),
    );

    for (let i = 1; i <= maxProducts; i++) {
      baseColumns.push({ header: `Produkt ${i} Navn`, width: 10 });
      baseColumns.push({ header: `Produkt ${i} Start`, width: 14 });
      baseColumns.push({ header: `Produkt ${i} Slutt`, width: 14 });
      baseColumns.push({ header: `Produkt ${i} Pris`, width: 10 });
      baseColumns.push({ header: `Produkt ${i} Antall`, width: 10 });
    }
  }

  // Build data rows
  const aoaData = rowData.map((booking) => {
    const row: (XLSX.CellObject | string | number)[] = [];

    // Add values for visible columns
    columnKeys.forEach((key) => {
      row.push(allColumnsConfig[key].getValue(booking));
    });

    // Add product columns if visible
    if (includeProducts) {
      const products =
        booking.program?.filter((p) => p.productID !== null) ?? [];
      for (let i = 0; i < maxProducts; i++) {
        const product = products[i];
        if (product) {
          row.push(product.productNameNO ?? product.productNameEN ?? "");
          row.push(createDateCell(product.start));
          row.push(createDateCell(product.end));
          row.push(createCurrencyCell(product.price));
          row.push(createNumberCell(product.quantity));
        } else {
          row.push("");
          row.push("");
          row.push("");
          row.push("");
          row.push("");
        }
      }
    }

    return row;
  });

  const headers = baseColumns.map((col) => col.header);
  const data = [headers, ...aoaData];

  // Create workbook and worksheet
  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bookings");

  // Set column widths
  ws["!cols"] = baseColumns.map((col) => ({ wch: col.width }));

  // Export file
  XLSX.writeFile(wb, `bookings-${dayjs().format("DD.MM.YYYY")}.xlsx`);
}
